import React, { useEffect, useState } from 'react';
import { ExternalLink, Handshake, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { proudPartnersData } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Proud Partners section — displays active partner links.
 * Reads from `/api/partners` first (admin-managed); falls back to mock.js.
 */
export const ProudPartners = () => {
  const [partners, setPartners] = useState(proudPartnersData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/partners`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && Array.isArray(data.partners) && data.partners.length > 0) {
            setPartners(data.partners);
          }
        }
      } catch (_) {
        // Silent — keep the mock fallback
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasPartners = partners && partners.length > 0;

  return (
    <section
      id="partners"
      className="py-24 px-6 bg-gunmetal-950 border-t border-steel-800/40"
      data-testid="proud-partners-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-amber-400/80 mb-4">
            <span className="h-px w-10 bg-amber-400/40" />
            MINDESIGN ENTERPRISES
            <span className="h-px w-10 bg-amber-400/40" />
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-steel-100 mb-4">
            Proud <span className="text-accent-red">Partners</span>
          </h2>
          <p className="max-w-2xl mx-auto text-steel-400 text-base">
            The alliances that sharpen the mission. Art, commerce, and community —
            standing in the same gunmetal light.
          </p>
        </div>

        {/* Grid / empty state */}
        {hasPartners ? (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="partners-grid"
          >
            {partners.map((p) => (
              <a
                key={p.id || p.url || p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gunmetal-800/50 backdrop-blur-sm border border-steel-700/30 hover:border-accent-red/60 rounded-lg overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-accent-red/20"
                data-testid={`partner-card-${p.id || p.name}`}
              >
                <div className="h-40 bg-gradient-to-br from-gunmetal-700 to-gunmetal-900 flex items-center justify-center overflow-hidden">
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="max-h-28 max-w-[70%] object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                  ) : (
                    <Handshake
                      className="text-accent-red/60 group-hover:text-accent-red transition-colors duration-300"
                      size={56}
                    />
                  )}
                </div>
                <div className="p-5 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-bold text-steel-100 group-hover:text-accent-red transition-colors">
                      {p.name}
                    </h3>
                    {p.tier && (
                      <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 bg-amber-400/10 text-amber-400 border border-amber-400/30 rounded-sm whitespace-nowrap">
                        {p.tier}
                      </span>
                    )}
                  </div>
                  {p.tagline && (
                    <p className="text-sm text-steel-400 leading-relaxed">
                      {p.tagline}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-accent-red pt-2">
                    <span>Visit site</span>
                    <ExternalLink
                      size={12}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div
            className="max-w-2xl mx-auto text-center bg-gunmetal-900/60 border border-dashed border-steel-700/60 rounded-lg p-10"
            data-testid="partners-empty-state"
          >
            <Handshake className="mx-auto text-accent-red mb-4" size={48} />
            <h3 className="text-xl font-bold text-steel-100 mb-2 tracking-wide">
              PARTNERSHIPS OPEN
            </h3>
            <p className="text-steel-400 mb-6">
              We're curating a hand-picked roster of brands, organizations, and
              independent creators aligned with the MINDESIGN mission. If that's you —
              let's talk.
            </p>
            <Button
              asChild
              className="bg-accent-red hover:bg-accent-red-dark text-white font-bold tracking-wider"
              data-testid="partners-contact-btn"
            >
              <a href="mailto:info@mindesignenterprises.com?subject=Proud%20Partner%20Inquiry">
                <Mail className="mr-2" size={16} />
                Become a Partner
              </a>
            </Button>
          </div>
        )}

        {loading && !hasPartners && (
          <p className="sr-only" aria-live="polite">Loading partners…</p>
        )}
      </div>
    </section>
  );
};

export default ProudPartners;
